import { Badge, Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { formatVolume } from '@features/prediction-markets/fakeMarketData';
import { MarketOutcome, PredictionMarket } from '@features/prediction-markets/types';
import Link from 'next/link';
import { memo, useMemo } from 'react';

interface Props {
  market: PredictionMarket;
  href: string;
}

const VISIBLE_OUTCOME_ROW_HEIGHT = 2;

const OutcomePreviewRow = memo(({ outcome }: { outcome: MarketOutcome }) => {
  return (
    <Flex alignItems="center" justifyContent="space-between" gap={2} w="full">
      <Text fontSize="sm" fontWeight="medium" color="text.primary" lineClamp={1} flex={1}>
        {outcome.label}
      </Text>
      <Text fontSize="sm" color="text.secondary" flexShrink={0}>
        {outcome.probability}%
      </Text>
      <Flex gap={1} flexShrink={0}>
        <Button
          size="2xs"
          fontSize="xs"
          borderRadius="md"
          px={2}
          bg="green.100"
          color="green.600"
          _hover={{ bg: 'green.500', color: 'white' }}
          _dark={{ bg: 'green.900/30', color: 'green.300', _hover: { bg: 'green.500', color: 'white' } }}
        >
          Yes
        </Button>
        <Button
          size="2xs"
          fontSize="xs"
          borderRadius="md"
          px={2}
          bg="red.100"
          color="red.600"
          _hover={{ bg: 'red.500', color: 'white' }}
          _dark={{ bg: 'red.900/30', color: 'red.300', _hover: { bg: 'red.500', color: 'white' } }}
        >
          No
        </Button>
      </Flex>
    </Flex>
  );
});
OutcomePreviewRow.displayName = 'OutcomePreviewRow';

const MarketCard = ({ market, href }: Props) => {
  const sortedOutcomes = useMemo(
    () => [...market.outcomes].sort((a, b) => b.probability - a.probability),
    [market.outcomes],
  );

  return (
    <Link href={href} style={{ textDecoration: 'none' }}>
      <Box
        bg="surface.muted"
        borderRadius="xl"
        p={4}
        cursor="pointer"
        _hover={{ bg: 'surface.hover' }}
        transition="background 0.15s"
        display="flex"
        flexDirection="column"
        gap={3}
        h="full"
      >
        <Flex alignItems="flex-start" gap={3}>
          <Image
            src={market.commodityImage}
            alt={market.commodityName}
            boxSize="36px"
            borderRadius="full"
            flexShrink={0}
          />
          <Text fontSize="sm" fontWeight="semibold" color="text.primary" lineClamp={2}>
            {market.question}
          </Text>
        </Flex>

        <Flex
          direction="column"
          gap={2}
          maxH={`calc(${VISIBLE_OUTCOME_ROW_HEIGHT} * 2rem)`}
          overflowY="auto"
          css={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}
        >
          {sortedOutcomes.map((outcome) => (
            <OutcomePreviewRow key={outcome.id} outcome={outcome} />
          ))}
        </Flex>

        <Flex alignItems="center" justifyContent="space-between" pt={1} mt="auto">
          <Flex alignItems="center" gap={2}>
            {market.isLive && (
              <Badge
                variant="subtle"
                colorPalette="red"
                fontSize="2xs"
                borderRadius="full"
                px={2}
              >
                <Flex alignItems="center" gap={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="red.500" />
                  LIVE
                </Flex>
              </Badge>
            )}
          </Flex>
          <Text fontSize="xs" color="text.secondary">
            {formatVolume(market.totalVolume)} Vol.
          </Text>
        </Flex>
      </Box>
    </Link>
  );
};

export default memo(MarketCard);
